import React, { useState, useEffect } from "react";
import { Box, Flex, Link, Text } from "@chakra-ui/react";
import { FormButton } from "../../../components/login/button.component";
import { TLocationHandler } from "../_types/location.type";
import { patchResendMailId, postUserValidation } from "../../../_services/user.service";
import { IResendValidationResponse, IValidationResponse } from "../../../_services/interface/user.interface";
import { SimpleFormError, SimpleFormValid } from "../../../components/login/formError.component";
import { IErrorResponse } from "../../../_services/interface/IDefaults";
import { authThrottleMessage } from "../../../utils/authThrottleMessage";
import { LoginForm } from "../login/LoginForm";
import { ValidationInput } from "./components/ValidationInput";

interface ValidationProps {
  locationHandler: TLocationHandler;
  email: string;
  userid: string;
}

export const ValidationForm = (props: ValidationProps) => {
  /**
   * GEN-3739: o contador nasce ZERADO e o botão de reenvio nasce LIBERADO.
   *
   * Ele nascia em 100s, travando a única ação real desta tela. Isso faria sentido se um e-mail
   * tivesse acabado de sair — e não tinha: o login devolvia `waitingValidation` sem disparar nada,
   * então a espera era em cima de um e-mail inexistente, e quem não aguentava os 100s ia embora
   * sem receber código nenhum. Agora quem despacha é o backend, no próprio login
   * (`ensureAccessMailSent`), e o freio anti-spam mora lá — cooldown de banco do GEN-3039. Aqui o
   * contador serve só para o usuário não martelar o botão, então ele passa a valer DEPOIS do
   * primeiro clique.
   */
  const [counter, setCounter] = React.useState(0);
  const [isResendDisabled, setResendDisabled] = useState<boolean>(false);
  const [isValidationBtnDisabled, setValidationBtn] = useState<boolean>(true);
  const [validationCode, setValidationCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [okMessage, setOkMessage] = useState("");

  //Timer to resend the information
  useEffect(() => {
    counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
    if (counter === 0) {
      setResendDisabled(false);
    }
  }, [counter]);

  const handleCode = (code: string) => {
    setValidationCode(code);
    if (code.length === 6) {
      setValidationBtn(false);
    } else {
      setValidationBtn(true);
    }
  };

  /**
   * Resend the validation information
   */
  const resendClick = async () => {
    setCounter(100);
    setResendDisabled(true);
    setOkMessage("");
    setErrorMessage("");

    const { status, response } = await patchResendMailId(props.userid);

    if (status === 200) {
      if ((response as IResendValidationResponse).mailsent) {
        // GEN-3039: o servidor tem cooldown de 60s no reenvio — dentro dessa janela ele responde
        // sucesso mas NÃO manda e-mail novo, porque o código anterior ainda serve. A redação
        // cobre os dois casos sem prometer um e-mail que pode não ter saído.
        // GEN-3739: "e-mail" e não "código" — o convidado recebe o link do convite por esta mesma
        // rota, e prometer um código a ele é o que originou o card.
        setOkMessage("Verifique seu e-mail. Se você já recebeu um há pouco, ele continua válido.");
      } else {
        setErrorMessage("Falha ao reenviar o e-mail.");
      }
    } else if ((response as IErrorResponse)?.message === "AUTH_TOO_MANY_ATTEMPTS") {
      /**
       * GEN-3039: a rota de reenvio passou a ter limite (dispara e-mail).
       *
       * O contador local é fixo em 100s, mas o limite do servidor é de 15 minutos por
       * identidade — reabilitar o botão em 100s fazia a própria tela instruir o usuário a
       * tentar de novo cedo demais, tomar outro 429 e repetir o ciclo a cada 100s até a janela
       * real fechar. Aqui o contador passa a obedecer o `retryAfter` que o servidor mandou, que
       * é o mesmo número já usado na mensagem.
       */
      const retryAfter = (response as IErrorResponse).retryAfter;
      if (retryAfter && retryAfter > 100) {
        setCounter(Math.ceil(retryAfter));
      }
      setErrorMessage(authThrottleMessage(retryAfter, "access"));
    } else {
      setErrorMessage("Falha ao reenviar o e-mail.");
    }
  };

  const sendValidation = async () => {
    setValidationBtn(true);
    setOkMessage("");
    setErrorMessage("");
    //Here the system dispatch the code
    const { status, response } = await postUserValidation(props.userid, {
      code: validationCode,
    });

    if (status === 200) {
      //Check if the user was validated or not
      if ((response as IValidationResponse).isValidated) {
        //Everything ok, so the system will send the user to the login page
        props.locationHandler(<LoginForm locationHandler={props.locationHandler} />);
      } else {
        setErrorMessage("Código inválido ou usuário já validado");
      }
    } else {
      const err = (response as IErrorResponse).message;

      switch (err) {
        case "VALIDATION_FAILED":
          setErrorMessage("Usuário inválido ou não possui acesso a essa organização");
          break;
        case "VALIDATION_ALREADY_ACTIVE":
          setErrorMessage("Usuário já se encontra validado.");
          break;
        case "VALIDATION_MAIL_FAILED":
          setErrorMessage("Falha ao gerar um novo código, não foi possível enviar o email, Tente reenviar novamente.");
          break;
        case "VALIDATION_CODE_EXPIRED":
          setErrorMessage("Código enviado foi expirado, o sistema irá enviar um novo código para o seu email.");
          break;
        /*
          GEN-3739: convidado que ainda não tem senha não se valida por código — validar aqui o
          deixaria validado E sem senha, jogando-o no "esqueci minha senha".

          A mensagem NÃO afirma que um e-mail acabou de sair: o backend deliberadamente não reemite
          o convite neste ponto (a rota é pública e reemitir daria a qualquer um com o `guid_user`
          uma forma de queimar o link da vítima). Quem precisa de um link novo usa o botão de
          reenvio abaixo — prometer aqui um envio que não houve seria repetir o defeito que este
          card corrige.
        */
        case "INVITE_PENDING":
          setErrorMessage(
            "Seu acesso é por convite: use o link que enviamos por e-mail para criar a sua senha. Não encontrou? Clique em Reenviar."
          );
          break;
        // GEN-3039: limite de tentativas do código de ativação.
        case "AUTH_TOO_MANY_ATTEMPTS":
          // Contexto "access" (GEN-3739): esta tela ATIVA a conta — não existe senha no fluxo para
          // "recuperar" — e atende também o convidado, cuja saída é um link, não um código.
          setErrorMessage(authThrottleMessage((response as IErrorResponse).retryAfter, "access"));
          break;
        default:
          setErrorMessage("Não foi possível validar o código. Tente novamente.");
          break;
      }
    }

    setValidationBtn(false);
  };

  return (
    <Flex fontFamily="Poppins" fontWeight={500} alignItems="center" mt="0px" w={396}>
      <Flex direction="column" w="100%">
        <Text fontSize="32px" textAlign="center">
          Validação
        </Text>
        <Box mt={12}>
          <Flex m="0px" direction="column" justifyContent="center">
            {/*
              GEN-3739: o texto não pode prometer um código. Quem chega aqui pode ser um cadastro
              não validado (recebe o código de 6 dígitos) ou um CONVIDADO que veio ao login em vez
              de clicar no link (recebe o convite de novo, porque o link é o único caminho que cria
              a senha dele). Quem decide é o servidor, e a tela não sabe qual dos dois — por isso a
              redação cobre os dois casos em vez de afirmar o que não pode garantir.
            */}
            <Text fontSize="12px" textAlign="center">
              Enviamos para o seu email <strong>{props.email}</strong> o que falta para concluir o seu acesso. (O email
              pode demorar alguns minutos para chegar até sua caixa)
            </Text>
            <Text fontSize="12px" textAlign="center" mt={2}>
              Se você recebeu um <strong>código de 6 dígitos</strong>, escreva-o abaixo. Se recebeu um{" "}
              <strong>convite com link</strong>, conclua o cadastro por ele — é lá que você cria a sua senha.
            </Text>
          </Flex>
          {errorMessage ? <SimpleFormError msg={errorMessage} /> : null}
          {okMessage ? <SimpleFormValid msg={okMessage} /> : null}
          <form>
            <Flex alignItems="stretch" justifyContent="center" direction="column" mt={12}>
              <ValidationInput digits={6} onChange={handleCode} />
              <Flex direction="column" gap={2} mt={12}>
                <FormButton disabled={isValidationBtnDisabled} onClick={sendValidation}>
                  Validar
                </FormButton>
                <FormButton disabled={isResendDisabled} onClick={resendClick}>
                  Reenviar
                </FormButton>

                {/* GEN-3739: o aviso de espera só existe depois do primeiro clique. */}
                {counter > 0 ? (
                  <Flex alignItems="center" justifyContent="center">
                    <Text fontSize="12px" opacity={0.5}>
                      Você pode pedir um novo envio em {counter} segundos
                    </Text>
                  </Flex>
                ) : null}

                {/*
                  GEN-3039: esta tela não tinha NENHUMA saída — o wizard de login é trocado por
                  estado em memória (src/pages/Login/index.tsx), a URL nunca muda, e o botão Voltar
                  do navegador sai da página de login inteira. Era inofensivo enquanto errar o
                  código não custava espera; agora um 429 legítimo pode ser de até 15 minutos, e sem
                  link o usuário fica preso numa tela sem ação possível, sem saber que F5 resolve.
                */}
                <Flex alignItems="center" justifyContent="center" fontSize="12px">
                  <Link
                    onClick={() => props.locationHandler(undefined)}
                    _hover={{ color: "#8789FF", cursor: "pointer" }}
                    color="#4B4EFF"
                  >
                    Retornar ao Login
                  </Link>
                </Flex>
              </Flex>
            </Flex>
          </form>
        </Box>
      </Flex>
    </Flex>
  );
};
